import React, { useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { useAdminNavigation } from '@/hooks/admin/useAdminNavigation';
import { useAppDispatch, useAppSelector } from '@/store';
import { toggleExpandedMenuId } from '@/store/slices/admin/adminUISlice';
import { selectExpandedMenuIds } from '@/store/selectors/adminUISelectors';
import { INavigationItem } from '@/types/admin/navigation';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { AnimatedAccordion } from '@/components/common/AnimatedAccordion';

interface AdminNavListProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const AdminNavList: React.FC<AdminNavListProps> = ({ currentPath, onNavigate }) => {
  const dispatch = useAppDispatch();
  const expandedMenuIds = useAppSelector(selectExpandedMenuIds);
  const { navigationItems, isPathActive, getFilteredSubNavItems } = useAdminNavigation();

  const handleNavigate = useCallback((path: string) => {
    onNavigate(path);
  }, [onNavigate]);

  const renderNavMenuItem = useCallback((item: INavigationItem) => {
    const isOpen = expandedMenuIds.includes(item.id);
    const isActive = isPathActive(item.path, item.subNavItems, currentPath);
    const filteredSubNavItems = getFilteredSubNavItems(item.subNavItems);
    const isLeaf = !filteredSubNavItems || filteredSubNavItems.length === 0;

    if (isLeaf) {
      return (
        <TouchableOpacity
          key={item.id}
          style={[styles.navMenuItem, isOpen && styles.navMenuItemOpen]}
          onPress={() => handleNavigate(item.path)}
          activeOpacity={0.7}
          accessibilityLabel={item.title}
          accessibilityHint={`Tap to navigate to ${item.title}`}
          accessibilityRole={'button' as any}
          accessibilityState={{ selected: isActive }}
        >
          <View style={styles.navMenuHeader}>
            <Text style={[styles.navMenuTitle, (isOpen || isActive) && styles.navMenuTitleOpen]}>
              {item.title}
            </Text>
          </View>
          {item.description && (
            <Text style={styles.navMenuDescription}>{item.description}</Text>
          )}
        </TouchableOpacity>
      );
    }

    return (
      <AnimatedAccordion
        key={item.id}
        isExpanded={isOpen}
        onToggle={() => dispatch(toggleExpandedMenuId(item.id))}
        containerStyle={[styles.navMenuItem, isOpen && styles.navMenuItemOpen]}
        headerStyle={styles.navMenuHeader}
        contentStyle={styles.navMenuChildren}
        accessibilityLabel={item.title}
        accessibilityHint={`Tap to ${isOpen ? 'collapse' : 'expand'} ${item.title} menu`}
        header={
          <Text style={[styles.navMenuTitle, (isOpen || isActive) && styles.navMenuTitleOpen]}>
            {item.title}
          </Text>
        }
        chevron={
          <SVG_COMPONENTS.CHEVRON_DOWN {...SVG_DIMENSIONS.CHEVRON_DOWN} color={ADMIN_COLORS.NAVIGATION.CHILD_TEXT} />
        }
      >
        {filteredSubNavItems.map((subItem) => {
          const isSubItemActive = currentPath.endsWith(subItem.path) || subItem.path === `/${currentPath.split('/')[1]}`;

          return (
            <TouchableOpacity
              key={subItem.path}
              style={styles.navMenuChild}
              onPress={() => handleNavigate(subItem.path)}
              accessibilityLabel={subItem.title}
              accessibilityHint={`Navigate to ${subItem.title}`}
              accessibilityRole={'button' as any}
              accessibilityState={{ selected: isSubItemActive }}
            >
              <View style={styles.navChildContent}>
                <Text style={[styles.navMenuChildText, isSubItemActive && styles.navMenuChildTextSelected]}>
                  {subItem.title}
                </Text>
                {isSubItemActive && <View style={styles.selectedIndicator} />}
              </View>
            </TouchableOpacity>
          );
        })}
      </AnimatedAccordion>
    );
  }, [expandedMenuIds, currentPath, isPathActive, getFilteredSubNavItems, dispatch, handleNavigate]);

  return (
    <View style={styles.navListContainer}>
      <View style={styles.navList}>
        {navigationItems.map(renderNavMenuItem)}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  navListContainer: {
    width: '100%',
    flex: 1,
  },
  navList: {
    width: '100%',
    gap: 12,
  },
  navMenuItem: {
    width: '100%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.NAVIGATION.ITEM_BORDER,
    paddingTop: 14,
    paddingHorizontal: 16,
    paddingBottom: 14,
  },
  navMenuItemOpen: {
    backgroundColor: ADMIN_COLORS.NAVIGATION.ITEM_BACKGROUND_OPEN,
    borderColor: ADMIN_COLORS.NAVIGATION.ITEM_BORDER_ACTIVE,
  },
  navMenuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
  },
  navMenuTitle: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.NAVIGATION.TITLE,
  },
  navMenuTitleOpen: {
    fontWeight: '600',
  },
  navMenuDescription: {
    fontFamily: 'System',
    fontWeight: '300',
    fontSize: 12,
    lineHeight: 18,
    color: ADMIN_COLORS.NAVIGATION.DESCRIPTION,
    marginTop: 8,
  },
  navMenuChildren: {
    gap: 8,
    paddingTop: 10,
  },
  navMenuChild: {
    paddingVertical: 0,
    position: 'relative',
  },
  navChildContent: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 21,
    justifyContent: 'space-between',
  },
  selectedIndicator: {
    position: 'absolute',
    left: -16,
    top: 0,
    width: 3,
    height: 21,
    backgroundColor: ADMIN_COLORS.NAVIGATION.SELECTED_INDICATOR,
  },
  navMenuChildText: {
    fontFamily: 'System',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.NAVIGATION.CHILD_TEXT,
    width: '100%',
  },
  navMenuChildTextSelected: {
    color: ADMIN_COLORS.NAVIGATION.CHILD_TEXT_SELECTED,
  },
});
